import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Loader2, Youtube, Unplug, CheckCircle2, AlertTriangle } from "lucide-react";

export default function YoutubeConnectionCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sp, setSp] = useSearchParams();

  const refresh = () =>
    api.get("/youtube/oauth/status").then((r) => setStatus(r.data));

  useEffect(() => {
    refresh();
    // Read ?yt= flag set by the OAuth callback redirect
    const yt = sp.get("yt");
    if (yt === "connected") {
      setError(null);
    } else if (yt === "error") {
      setError(`OAuth failed (${sp.get("reason") || "unknown"})`);
    }
    if (yt) {
      // strip the query so it doesn't survive a page reload
      const next = new URLSearchParams(sp);
      next.delete("yt");
      next.delete("reason");
      setSp(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/youtube/oauth/authorize");
      window.location.assign(r.data.authorize_url);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      setLoading(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect YouTube? Pending uploads will fail."))
      return;
    setLoading(true);
    try {
      await api.post("/youtube/oauth/disconnect");
      await refresh();
    } finally {
      setLoading(false);
    }
  };

  if (!status) {
    return (
      <div className="flex items-center gap-2 text-stone-500">
        <Loader2 className="h-4 w-4 animate-spin" /> loading…
      </div>
    );
  }

  return (
    <Card
      data-testid="youtube-connection-card"
      className="border-orange-200 bg-white/80"
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Youtube className="h-5 w-5 text-red-600" />
          YouTube channel
        </CardTitle>
        <CardDescription>
          Auto-publish your finished videos directly to your channel.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error ? (
          <div
            data-testid="youtube-error"
            className="mb-3 flex items-start gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        {status.connected ? (
          <div
            data-testid="youtube-connected"
            className="flex flex-wrap items-center gap-4 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3"
          >
            {status.channel_thumbnail ? (
              <img
                src={status.channel_thumbnail}
                alt={status.channel_title}
                className="h-12 w-12 rounded-full border border-emerald-300"
              />
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-200 text-emerald-800">
                <Youtube className="h-6 w-6" />
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-stone-800">
                  {status.channel_title}
                </span>
                <Badge className="bg-emerald-600 hover:bg-emerald-600">
                  <CheckCircle2 className="mr-1 h-3 w-3" /> connected
                </Badge>
              </div>
              <div className="mt-0.5 text-xs text-stone-500">
                {status.subscriber_count?.toLocaleString() || 0} subscribers ·{" "}
                {status.video_count?.toLocaleString() || 0} videos
              </div>
            </div>
            <Button
              variant="outline"
              onClick={disconnect}
              disabled={loading}
              data-testid="youtube-disconnect"
              className="border-red-300 text-red-700 hover:bg-red-50"
            >
              {loading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Unplug className="mr-2 h-4 w-4" />
              )}
              Disconnect
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-start gap-3 rounded-lg border border-dashed border-orange-300 bg-white/60 p-4">
            <div className="text-sm text-stone-700">
              Sign in with the Google account that owns the{" "}
              <strong>Dom's Journey</strong> channel. We request{" "}
              <code className="rounded bg-stone-100 px-1 text-xs">
                youtube.upload
              </code>
              ,{" "}
              <code className="rounded bg-stone-100 px-1 text-xs">
                youtube
              </code>
              , and{" "}
              <code className="rounded bg-stone-100 px-1 text-xs">
                youtube.readonly
              </code>{" "}
              to upload videos and add them to your playlists.
            </div>
            <Button
              onClick={connect}
              disabled={loading}
              data-testid="youtube-connect"
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {loading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Youtube className="mr-2 h-4 w-4" />
              )}
              Connect YouTube
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
